import { useState, useEffect } from "react";
import { useLogin } from "../pages/contexts/LoginContext";
import config from "../config";
import { getCookie } from "./utils";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { useNavigate } from 'react-router-dom';

const uclablue = "#2774AE"; // UCLA 蓝色
const uclagold = "#FFB300"; // UCLA 金色

const ProfilePage = () => {
  const { user, setUser } = useLogin();
  const [previewUrl, setPreviewUrl] = useState("");
  const [newAvatar, setNewAvatar] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editedUsername, setEditedUsername] = useState("");

  // 修改：workouts现在存储原始数据
  const [workouts, setWorkouts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.avatarUrl) setPreviewUrl(user.avatarUrl);
    if (user?.username) setEditedUsername(user.username);

    // 设置 overflow 为 auto
    document.body.style.overflow = 'auto';

    // 尝试加载缓存的 workout 数据
    const cachedWorkouts = localStorage.getItem('cachedWorkouts');

    if (cachedWorkouts) {
      try {
        const parsedWorkouts = JSON.parse(cachedWorkouts);
        if (Array.isArray(parsedWorkouts)) {
          setWorkouts(parsedWorkouts);
          return;
        } else if (parsedWorkouts && typeof parsedWorkouts === 'object') {
          setWorkouts([parsedWorkouts]);
          return;
        }
      } catch (error) {
        console.error('Error parsing cached workouts:', error);
        // 继续请求
      }
    }

    // 如果缓存无效，发起请求从后端获取 workouts
    const fetchWorkouts = async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/api/workouts/`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
          },
        });

        if (res.ok) {
          const data = await res.json();
          setWorkouts(data);
          localStorage.setItem('cachedWorkouts', JSON.stringify(data));
        } else {
          console.error('Failed to fetch workouts:', res.status);
        }
      } catch (error) {
        console.error('Error fetching workouts:', error);
      }
    };

    fetchWorkouts();
  }, [user]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewAvatar(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setIsSaving(true);

    const formData = new FormData();
    if (newAvatar) formData.append("avatar", newAvatar);
    formData.append("username", editedUsername);

    try {
      const res = await fetch(`${config.apiBaseUrl}/api/upload-profile/`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: {
          "X-CSRFToken": getCookie("csrftoken"),
        },
      });
      const data = await res.json();

      // 更新 user 状态
      setUser((prev) => ({
        ...prev,
        avatarUrl: data.avatarUrl ?? prev.avatarUrl,
        username: data.username ?? prev.username,
      }));

      setNewAvatar(null);
      setIsEditingUsername(false);
    } catch (err) {
      alert("Saving failed. Please try again.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // 修改：根据新的数据格式计算图表数据
  const calculateChartData = () => {
    // console.log('Raw workouts data:', workouts);
    // console.log('Current user:', user);
    // console.log('Is workouts array?', Array.isArray(workouts));
    // console.log('Workouts length:', workouts?.length);

    if (!Array.isArray(workouts) || workouts.length === 0 || !user) {
      // console.log('No workouts data or not array or no user');
      return { frequencyData: [], timeData: [] };
    }

    // 先看看第一个workout的结构
    if (workouts.length > 0) {
      // console.log('First workout structure:', workouts[0]);
      // console.log('Available keys:', Object.keys(workouts[0]));
    }

    // 首先筛选出当前用户的锻炼记录
    const currentUserWorkouts = workouts.filter(workout => {
      // 检查workout是否属于当前用户
      const workoutUserId = workout.last_modified_by?.id || workout.user?.id || workout.user_id;
      const currentUserId = user.id || user.user_id;

      // console.log('Workout user ID:', workoutUserId, 'Current user ID:', currentUserId);

      return workoutUserId === currentUserId;
    });

    // console.log('Current user workouts:', currentUserWorkouts);

    if (currentUserWorkouts.length === 0) {
      // console.log('No workouts found for current user');
      return { frequencyData: [], timeData: [] };
    }

    // 然后从当前用户的记录中筛选出举重相关的锻炼
    const weightLiftingWorkouts = currentUserWorkouts.filter(workout => {
      // console.log('Checking workout:', workout);

      // 根据你的实际数据结构，workout_type是直接字段
      const workoutType = workout.workout_type ||
        workout.type?.name ||
        workout.exercise_type?.name ||
        workout.name ||
        workout.exercise_name ||
        '';

      // console.log('Workout type found:', workoutType);

      const isWeightLifting = workoutType.toLowerCase().includes('weight') ||
        workoutType.toLowerCase().includes('lifting') ||
        workoutType.toLowerCase().includes('strength') ||
        workoutType.toLowerCase().includes('gym');

      // console.log('Is weight lifting:', isWeightLifting);
      return isWeightLifting;
    });

    // console.log('Filtered weight lifting workouts:', weightLiftingWorkouts);

    if (weightLiftingWorkouts.length === 0) {
      console.log('No weight lifting workouts found');
      return { frequencyData: [], timeData: [] };
    }

    // 按日期分组
    const workoutsByDate = {};
    weightLiftingWorkouts.forEach(workout => {
      // console.log('Processing workout for date grouping:', workout);

      // 根据你的数据结构，直接使用 date 字段
      const timestamp = workout.date ||
        workout.timestamp ||
        workout.created_at ||
        workout.workout_date ||
        workout.start_time ||
        workout.time;

      // console.log('Timestamp found:', timestamp);

      if (timestamp) {
        // 如果是日期格式 "2025-06-20"，直接使用
        const date = timestamp.includes('T') ?
          new Date(timestamp).toISOString().split('T')[0] :
          timestamp;
        // console.log('Parsed date:', date);

        if (!workoutsByDate[date]) {
          workoutsByDate[date] = [];
        }
        workoutsByDate[date].push(workout);
      }
    });

    // console.log('Workouts grouped by date:', workoutsByDate);

    const workoutDates = Object.keys(workoutsByDate).sort();
    if (workoutDates.length === 0) return { frequencyData: [], timeData: [] };

    const startDate = new Date(workoutDates[0]);
    const today = new Date();

    // 计算从第一天到今天的总天数
    const totalDays = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24)) + 1;

    // 计算有健身记录的天数
    const workoutDays = workoutDates.length;

    // 计算健身频率（百分比）
    const frequency = ((workoutDays / totalDays) * 100).toFixed(1);

    // 柱状图数据 - 显示健身频率
    const frequencyData = [
      {
        name: 'Workout Frequency',
        percentage: parseFloat(frequency),
        totalDays: totalDays,
        workoutDays: workoutDays
      }
    ];

    // 折线图数据 - 每日举重时间（连续日期）
    const timeData = [];
    const currentDate = new Date(startDate);
    const endDate = new Date(today);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const dayWorkouts = workoutsByDate[dateStr] || [];

      let totalTime = 0;
      let bodyPart = '';
      if (dayWorkouts.length > 0) {
        totalTime = dayWorkouts.reduce((sum, workout) => {
          // 尝试多种可能的时长字段
          const duration = workout.duration ||
            workout.duration_minutes ||
            workout.time ||
            workout.workout_duration ||
            workout.length ||
            0;

          // console.log('Workout duration found:', duration, 'for workout:', workout);
          return sum + duration;
        }, 0);
        bodyPart = dayWorkouts[0]?.weightlift_part || '';
      }

      // console.log('Total time for', dateStr, ':', totalTime);

      timeData.push({
        date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        time: totalTime,
        fullDate: dateStr,
        hasWorkout: totalTime > 0,
        weightlift_part: bodyPart
      });

      // 移到下一天
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return { frequencyData, timeData };
  };

  const { frequencyData, timeData } = calculateChartData();

  const LineTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const bodyPart = payload[0].payload?.weightlift_part || '';
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-sm text-green-600">{`Weight Lifting Time: ${payload[0].value}m`}</p>
          {bodyPart && (
            <p className="text-sm text-gray-500">{`Body Part: ${bodyPart}`}</p>
          )}
        </div>
      );
    }
    return null;
  };

  if (!user) return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto mt-12 px-6">
      {/* 原有的个人资料部分 */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Profile</h1>

          <div className="flex space-x-4">

            {/* 返回上一级 */}
            <button
              onClick={() => window.history.back()}
              title="Back"
              className="p-2 rounded hover:bg-gray-200"
              aria-label="Go Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={() => navigate('/fit')}
              title="Go to Fit"
              className="p-2 rounded hover:bg-gray-200"
              aria-label="Go to Fit page"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 512"
                className="h-6 w-6 text-gray-600 fill-current"
              >
                <path d="M96 64c0-17.7 14.3-32 32-32l32 0c17.7 0 32 14.3 32 32l0 160 0 64 0 160c0 17.7-14.3 32-32 32l-32 0c-17.7 0-32-14.3-32-32l0-64-32 0c-17.7 0-32-14.3-32-32l0-64c-17.7 0-32-14.3-32-32s14.3-32 32-32l0-64c0-17.7 14.3-32 32-32l32 0 0-64zm448 0l0 64 32 0c17.7 0 32 14.3 32 32l0 64c17.7 0 32 14.3 32 32s-14.3 32-32 32l0 64c0 17.7-14.3 32-32 32l-32 0 0 64c0 17.7-14.3 32-32 32l-32 0c-17.7 0-32-14.3-32-32l0-160 0-64 0-160c0-17.7 14.3-32 32-32l32 0c17.7 0 32 14.3 32 32zM416 224l0 64-192 0 0-64 192 0z" />
              </svg>
            </button>

            {/* Home */}
            <button
              onClick={() => navigate('/')}
              title="Home"
              className="p-2 rounded hover:bg-gray-200"
              aria-label="Go to Home"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-5H9v5a2 2 0 0 1-2 2H3z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-6 mb-6">
          <div className="relative">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Avatar"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 shadow"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 cursor-pointer"
              title="Change picture"
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              {isEditingUsername ? (
                <input
                  value={editedUsername}
                  onChange={(e) => setEditedUsername(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              ) : (
                <p className="text-lg font-medium text-gray-700">{user.username}</p>
              )}
              <button
                onClick={() => setIsEditingUsername((prev) => !prev)}
                title="Edit username"
              >
                📝
              </button>
            </div>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>

        <div className="w-full mb-4">
          <div className="text-sm text-gray-500 mb-1">Workout Progress</div>
          <div className="relative h-3 rounded-full overflow-hidden"
            style={{ backgroundColor: uclablue }}>
            <div
              className="absolute left-0 top-0 h-full transition-all duration-500"
              style={{
                width: `${frequencyData[0]?.percentage || 0}%`,
                backgroundColor: uclagold,
              }}
            ></div>
          </div>
          <div className="text-right text-xs text-gray-400 mt-1">
            {frequencyData[0]?.workoutDays || 0}/{frequencyData[0]?.totalDays || 0} days (
            {frequencyData[0]?.percentage || 0}%)
          </div>
        </div>

        <div className="mt-8 rounded-xl pb-6">
          <div className="text-sm text-gray-500 mb-1">Strength Training Records</div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-blue-600">
                {frequencyData[0]?.percentage || 0}%
              </div>
              <div className="text-sm text-gray-600">Frequency</div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-green-600">
                {timeData.filter(d => d.fullDate).length}
              </div>
              <div className="text-sm text-gray-600">Days</div>
              <div className="text-sm text-gray-500">
  YOT: {(timeData.filter(d => d.fullDate).length / 365).toFixed(2)} years
</div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-purple-600">
                {timeData.filter(d => d.hasWorkout).reduce((sum, item) => sum + item.time, 0)}
              </div>
              <div className="text-sm text-gray-600">Time</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-1.5 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium shadow-sm disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* 健身统计图表部分 */}
      <div className="bg-white rounded-2xl shadow-xl p-2">
        {frequencyData.length > 0 && timeData.length > 0 ? (
          <>
            {/* 折线图 - 每日举重时间 */}
            <div className="bg-white rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                Weight Lifting Time over Days
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="uclaAreaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2774AE" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#FFB300" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>

                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      stroke="#374151"
                    />
                    <YAxis
                      label={{ value: 'Time (m)', angle: -90, position: 'insideLeft', fill: '#1f2937' }}
                      stroke="#374151"
                    />
                    <Tooltip content={<LineTooltip />} />

                    <Area
                      type="monotone"
                      dataKey="time"
                      stroke="none"
                      fill="url(#uclaAreaGradient)"
                      connectNulls={false}
                    />

                    <Line
                      type="monotone"
                      dataKey="time"
                      stroke="#2774AE"
                      strokeWidth={3}
                      connectNulls={false}
                      dot={false}
                      activeDot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 text-center text-sm text-gray-800">
                Days: <span className="font-bold">
                  {timeData.filter(d => d.hasWorkout).length}
                </span> Avg: <span className="font-bold">{
                  timeData.filter(d => d.hasWorkout).length > 0
                    ? (timeData.filter(d => d.hasWorkout).reduce((sum, item) => sum + item.time, 0) / timeData.filter(d => d.hasWorkout).length).toFixed(1)
                    : 0
                }</span> m
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">📊</div>
            <p className="text-gray-600">No Weight Lifting Records</p>
            <p className="text-sm text-gray-500 mt-2">Start Training to See Your Stats</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;